import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Main } from 'src/components/commons/Main';
import Table from 'src/components/commons/Table/Table';
import StatusChip from 'src/components/commons/StatusChip';
import { getFounderMatches, getVendorWithUserAnswersForAdmin, updateFounderVendorMatchStatus } from 'src/api/vendorServices';
import { formatPhone } from 'src/helpers';
import { Box, Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DashboardDetails from 'src/components/campaigns/DashboardDetails';
import { getMatchActionButtons } from 'src/helpers/matchActions.jsx';
import { renderSafeValue } from 'src/helpers';

export default function FounderMatches() {
  const { authUser } = useSelector(({ auth }) => auth);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewDetails, setViewDetails] = useState(null);

  useEffect(() => {
    async function fetchMatches() {
      try {
        setLoading(true);
        setError(false);
        const matchesArr = await getFounderMatches(authUser.id, { page: page + 1, limit: rowsPerPage });
        setMatches(matchesArr || []);
        setTotalCount((matchesArr && matchesArr.length > 0 && matchesArr[0].total_count) || 0);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    if (authUser?.id) fetchMatches();
  }, [authUser?.id, page, rowsPerPage]);

  const handleViewVendor = async (row) => {
    const vendorServiceId = row.vendor_service_id || row.vendor_service || row.id;
    try {
      const details = await getVendorWithUserAnswersForAdmin(vendorServiceId);
      setViewDetails(details);
      setViewOpen(true);
    } catch (e) {
      setViewDetails(null);
      setViewOpen(false);
    }
  };

  const handleAccept = async (row) => {
    try {
      const updatedMatch = await updateFounderVendorMatchStatus(row.id, { status: 'active' });
      // Update the local state immediately
      setMatches(prevMatches => 
        prevMatches.map(match => 
          match.id === row.id 
            ? { ...match, status: 'active' }
            : match
        )
      );
    } catch (e) {
      console.error('Error accepting match:', e);
    }
  };

  const handleDecline = async (row) => {
    try {
      const updatedMatch = await updateFounderVendorMatchStatus(row.id, { 
        status: 'declined',
        call_status: 'declined'
      });
      // Update the local state immediately
      setMatches(prevMatches => 
        prevMatches.map(match => 
          match.id === row.id 
            ? { ...match, status: 'declined', call_status: 'declined' }
            : match
        )
      );
    } catch (e) {
      console.error('Error declining match:', e);
    }
  };

  const columns = [
    { header: 'Offered Service', accessor: 'offered_service', align: 'left', render: (_, row) => renderSafeValue(row.offered_service, 'offered_service') },
    { 
      header: 'Match Status', 
      accessor: 'status', 
      align: 'center', 
      render: (_, row) => <StatusChip status={row.status} />
    },
    { 
      header: 'Call Status', 
      accessor: 'call_status', 
      align: 'center', 
      render: (_, row) => <StatusChip status={row.call_status} />
    },
    { header: 'Vendor Name', accessor: 'vendor_name', align: 'center' },
    { header: 'Vendor Email', accessor: 'vendor_email', align: 'center' },
    { header: 'Vendor Business Phone', accessor: 'vendor_business_phone', align: 'center', render: formatPhone },
    { header: 'Vendor Personal Phone', accessor: 'vendor_personal_phone', align: 'center', render: formatPhone },
    { 
      header: 'Action', 
      accessor: 'action', 
      align: 'center', 
      render: (_, row) => getMatchActionButtons({
        row,
        onView: handleViewVendor,
        onAccept: handleAccept,
        onDecline: handleDecline
      })
    },
  ];

  return (
    <Main>
      <Box display={'flex'} justifyContent='space-between' p={4} borderBottom={'1px solid rgb(239 232 251)'}>
        <Typography component={'h2'} variant='h6'>
          Matches Found
        </Typography>
      </Box>
      <Box p={4}>
        <Table
          columns={columns}
          data={matches}
          loading={loading}
          error={error}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          stickyHeader
          className='border-0'
          emptyStateMessage='No matches found.'
        />
      </Box>
      {/* Vendor Details Modal */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Vendor Details</DialogTitle>
        <DialogContent>
          {viewDetails && <DashboardDetails details={viewDetails} userType="FOUNDER" />}
        </DialogContent>
      </Dialog>
    </Main>
  );
} 